import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import { API_URL } from "../config";
import { toast, ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";

/**
 * ─── Member Signup Page ───
 *
 * Public page where anyone can create a member account.
 * Members can then log in and book activities with auto-filled info.
 */

function Signup() {
  const [name, setName] = useState("");
  const [username, setUsername] = useState("");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [sex, setSex] = useState("");
  const [age, setAge] = useState("");
  const [phone, setPhone] = useState("");
  const [error, setError] = useState("");
  const { login } = useAuth();
  const navigate = useNavigate();

  const validatePassword = (pw) => {
    if (pw.length < 8) return "Passordet må være minst 8 tegn";
    if ((pw.match(/[0-9]/g) || []).length < 2) return "Passordet må inneholde minst 2 tall";
    if (!/[!@#$%^&*()_+\-=[\]{};':"\\|,.<>/?~`]/.test(pw)) return "Passordet må inneholde minst 1 spesialtegn (!@#$% osv.)";
    return null;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError("");

    const pwError = validatePassword(password);
    if (pwError) {
      setError(pwError);
      return;
    }

    try {
      const res = await fetch(`${API_URL}/api/auth/register`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ name, username, email, password, sex, age, phone }),
      });

      const data = await res.json();

      if (!res.ok) {
        setError(data.error || "Noe gikk galt");
        return;
      }

      // Auto-login after successful registration
      const loginRes = await fetch(`${API_URL}/api/auth/login`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify({ username, password }),
      });

      if (loginRes.ok) {
        const loginData = await loginRes.json();
        login(loginData.username, loginData.role, loginData.email, loginData.name, loginData.sex, loginData.age, loginData.phone);
        toast.success("✅ Konto opprettet! Du er nå logget inn.");
        setTimeout(() => navigate("/"), 1500);
      } else {
        toast.success("✅ Konto opprettet! Du kan nå logge inn.");
        setTimeout(() => navigate("/login"), 1500);
      }
    } catch (err) {
      setError("Kan ikke koble til serveren. Er den startet?");
    }
  };

  return (
    <div className="container d-flex justify-content-center align-items-center" style={{ minHeight: "80vh" }}>
      <div className="card shadow" style={{ width: "480px" }}>
        <div className="card-body p-4">
          <h3 className="card-title text-center mb-4">📝 Bli Medlem</h3>

          {error && <div className="alert alert-danger">{error}</div>}

          <form onSubmit={handleSubmit} noValidate>
            <div className="mb-3">
              <label htmlFor="signup-name" className="form-label">Fullt navn</label>
              <input
                id="signup-name"
                type="text"
                className="form-control"
                value={name}
                onChange={(e) => setName(e.target.value)}
                placeholder="Ditt fulle navn"
                autoComplete="name"
                required
              />
            </div>
            <div className="mb-3">
              <label htmlFor="signup-username" className="form-label">Brukernavn</label>
              <input
                id="signup-username"
                type="text"
                className="form-control"
                value={username}
                onChange={(e) => setUsername(e.target.value)}
                placeholder="Velg et brukernavn"
                autoComplete="username"
                required
              />
            </div>
            <div className="mb-3">
              <label htmlFor="signup-email" className="form-label">E-post</label>
              <input
                id="signup-email"
                type="email"
                className="form-control"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="din@epost.no"
                autoComplete="email"
                required
              />
            </div>
            <div className="mb-3">
              <label htmlFor="signup-phone" className="form-label">Telefon <span className="text-muted fw-normal">(valgfritt)</span></label>
              <input
                id="signup-phone"
                type="tel"
                className="form-control"
                value={phone}
                onChange={(e) => setPhone(e.target.value.replace(/\D/g, ""))}
                placeholder="12345678"
                autoComplete="tel"
                inputMode="numeric"
                maxLength={15}
              />
            </div>
            <div className="row mb-3">
              <div className="col">
                <label htmlFor="signup-sex" className="form-label">Kjønn</label>
                <select
                  id="signup-sex"
                  className="form-select"
                  value={sex}
                  onChange={(e) => setSex(e.target.value)}
                  required
                >
                  <option value="">Velg kjønn</option>
                  <option value="Mann">Mann</option>
                  <option value="Kvinne">Kvinne</option>
                </select>
              </div>
              <div className="col">
                <label htmlFor="signup-age" className="form-label">Alder</label>
                <select
                  id="signup-age"
                  className="form-select"
                  value={age}
                  onChange={(e) => setAge(e.target.value)}
                  required
                >
                  <option value="">Velg alder</option>
                  <option value="7-12">7–12</option>
                  <option value="13-18">13–18</option>
                  <option value="19-30">19–30</option>
                  <option value="30+">30+</option>
                </select>
              </div>
            </div>
            <div className="mb-3">
              <label htmlFor="signup-password" className="form-label">Passord</label>
              <input
                id="signup-password"
                type="password"
                className="form-control"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                placeholder="Minst 8 tegn, 2 tall, 1 spesialtegn"
                autoComplete="new-password"
                minLength={8}
                required
              />
              {password.length > 0 && (
                <ul className="list-unstyled mt-2 mb-0 small">
                  <li className={password.length >= 8 ? "text-success" : "text-danger"}>
                    {password.length >= 8 ? "✅" : "❌"} Minst 8 tegn ({password.length}/8)
                  </li>
                  <li className={(password.match(/[0-9]/g) || []).length >= 2 ? "text-success" : "text-danger"}>
                    {(password.match(/[0-9]/g) || []).length >= 2 ? "✅" : "❌"} Minst 2 tall ({(password.match(/[0-9]/g) || []).length}/2)
                  </li>
                  <li className={/[!@#$%^&*()_+\-=[\]{};':"\\|,.<>/?~`]/.test(password) ? "text-success" : "text-danger"}>
                    {/[!@#$%^&*()_+\-=[\]{};':"\\|,.<>/?~`]/.test(password) ? "✅" : "❌"} Minst 1 spesialtegn (!@#$% osv.)
                  </li>
                </ul>
              )}
            </div>
            <button type="submit" className="btn btn-success w-100 mb-3">
              Registrer deg
            </button>
          </form>

          <p className="text-center text-muted mb-0">
            Allerede medlem?{" "}
            <a href="/login" className="text-decoration-none">Logg inn</a>
          </p>
        </div>
      </div>
      <ToastContainer position="top-center" autoClose={3000} hideProgressBar={false} theme="colored" />
    </div>
  );
}

export default Signup;
